import { useId, useState, type FormEvent } from "react";
import type { AppointmentView, BookAppointmentBody } from "@gioia365/contracts";
import { callbackInstantOf } from "@gioia365/domain/callbacks";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../design/components/Button";
import { Dialog } from "../design/components/Dialog";
import { Reason } from "../design/components/Reason";
import { Segmented } from "../design/components/Segmented";
import { Select } from "../design/components/Select";
import { TextField } from "../design/components/TextField";
import { useToast } from "../design/components/Toast";
import { messageForError } from "../i18n/errors";
import { formatNumericDateTime, formatNumericTime, localDayOf } from "../i18n/format";
import { useT } from "../i18n/locale";
import { useLeadView } from "../leads/lead-queries";
import type { TabContext } from "../session/tab-context";
import { newRequestId } from "../telephony/call-session-state";
import { appointmentOptionsQuery, useBookAppointment, useChangeAppointment } from "./appointments-queries";
import "./appointments.css";

// La finestra «Prenota disco o demo» (piano M6, R7, Task 6; SEZ-11, TASK-11, TASK-12): che cosa si prenota, giorno e ora nel fuso
// dell'azienda, la durata (30 per la disco, 60 per la demo, da 5 a 480), il collega da invitare e l'email del Lead quando manca.
// Prima di salvare dice che cosa succederà: con Google Calendar collegato e l'email, l'evento con il Meet e gli inviti; altrimenti
// l'appuntamento resta soltanto in Gioia365. La stessa finestra, con l'appuntamento, fa «Sposta». Il server rivalida tutto: un
// orario fuori dal calendario lavorativo, un appuntamento già aperto o una versione vecchia tornano con il loro motivo.

export type AppointmentKind = "disco" | "demo";
const DEFAULT_MINUTES: Record<AppointmentKind, number> = { disco: 30, demo: 60 };

type Props = {
  tab: TabContext;
  leadId: string;
  timeZone: string;
  /** Senza: una prenotazione nuova; con: «Sposta» di quell'appuntamento. */
  moving?: AppointmentView;
  /** Il tipo proposto (dall'esito della postazione). */
  initialKind?: AppointmentKind;
  /** La chiamata da cui nasce la prenotazione, dalla postazione. */
  callId?: string;
  /** Dalla postazione: l'esito si salva prima della prenotazione; `false` se il server lo rifiuta (lo dice la postazione). */
  beforeSave?: () => Promise<boolean>;
  onSaved?: (appointment: AppointmentView) => void;
  onClose: () => void;
};

/** Il giorno dopo nel fuso dell'azienda, alle 10:00: un punto di partenza che la persona cambia. */
function initialSlot(timeZone: string, moving: AppointmentView | undefined): { day: string; time: string } {
  if (moving) return { day: localDayOf(timeZone, moving.startsAt), time: formatNumericTime(timeZone, moving.startsAt) };
  return { day: localDayOf(timeZone, new Date(Date.now() + 86_400_000).toISOString()), time: "10:00" };
}

export function BookAppointmentDialog({ tab, leadId, timeZone, moving, initialKind, callId, beforeSave, onSaved, onClose }: Props) {
  const t = useT();
  const toast = useToast();
  const view = useLeadView();
  const ids = useId();
  const options = useQuery({ ...appointmentOptionsQuery(tab, leadId, view), enabled: moving === undefined });
  const book = useBookAppointment(tab, leadId, view);
  const change = useChangeAppointment(tab, leadId, view);
  // Una chiave per apertura: un nuovo invio dopo un guasto è la stessa prenotazione.
  const [requestId] = useState(newRequestId);
  const [kind, setKind] = useState<AppointmentKind>(moving?.kind ?? initialKind ?? "disco");
  const [slot, setSlot] = useState(() => initialSlot(timeZone, moving));
  const [minutes, setMinutes] = useState<string | null>(null);
  const [colleague, setColleague] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const pending = book.isPending || change.isPending;
  const data = options.data;
  const shownMinutes = minutes ?? String(DEFAULT_MINUTES[kind]);
  const leadEmail = data?.leadEmail ?? null;
  const hasEmail = leadEmail !== null || email.trim() !== "";
  const kindName = t(`appointments.kind.${kind}`);

  let outcome: string | null = null;
  if (data) outcome = !data.calendarConnected ? t("appointments.dialog.withoutCalendar") : hasEmail ? t("appointments.dialog.withCalendar") : t("appointments.dialog.withoutEmail");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending) return;
    setError(null);
    const instant = callbackInstantOf(slot.day, slot.time, timeZone);
    const duration = Number(shownMinutes);
    if (!instant || !Number.isInteger(duration) || duration < 5 || duration > 480) {
      setError(t("appointments.dialog.invalidTime"));
      return;
    }
    try {
      if (beforeSave && !(await beforeSave())) {
        onClose();
        return;
      }
      let saved: AppointmentView;
      if (moving) {
        saved = (await change.mutateAsync({ taskId: moving.id, command: { action: "reschedule", startsAt: instant.toISOString(), version: moving.version } })).appointment;
        toast.show(t("appointments.dialog.moved", { kind: kindName, when: formatNumericDateTime(timeZone, saved.startsAt) }), "success");
      } else {
        const body: BookAppointmentBody = {
          kind,
          startsAt: instant.toISOString(),
          minutes: duration,
          requestId,
          ...(colleague ? { inviteMembershipId: colleague } : {}),
          ...(leadEmail === null && email.trim() !== "" ? { leadEmail: email.trim() } : {}),
          ...(callId ? { callId } : {}),
        };
        saved = (await book.mutateAsync(body)).appointment;
        toast.show(t("appointments.dialog.saved", { kind: kindName, when: formatNumericDateTime(timeZone, saved.startsAt) }), "success");
      }
      onSaved?.(saved);
      onClose();
    } catch (failure) {
      setError(messageForError(t, failure));
    }
  }

  const title = moving ? t("appointments.dialog.moveTitle", { kind: kindName, when: formatNumericDateTime(timeZone, moving.startsAt) }) : t("appointments.dialog.title");
  return (
    <Dialog open size="wide" title={title} onClose={onClose}>
      <form className="g-appointment-form" onSubmit={(event) => void submit(event)} noValidate aria-busy={pending}>
        {error ? (
          <Reason tone="warning" role="alert">
            {error}
          </Reason>
        ) : null}
        {moving === undefined ? (
          <Segmented
            label={t("appointments.dialog.kind")}
            options={(["disco", "demo"] as const).map((value) => ({ value, label: t(`appointments.kind.${value}`) }))}
            value={kind}
            onChange={(value) => setKind(value === "demo" ? "demo" : "disco")}
          />
        ) : null}
        <div className="g-appointment-form__row">
          <TextField id={`${ids}-day`} label={t("appointments.dialog.day")} type="date" value={slot.day} onChange={(event) => setSlot({ ...slot, day: event.target.value })} required />
          <TextField id={`${ids}-time`} label={t("appointments.dialog.time")} type="time" step={900} value={slot.time} onChange={(event) => setSlot({ ...slot, time: event.target.value })} required />
          {moving === undefined ? (
            <TextField
              id={`${ids}-minutes`}
              label={t("appointments.dialog.minutes")}
              hint={t("appointments.dialog.minutesHint")}
              type="number"
              inputMode="numeric"
              min={5}
              max={480}
              step={5}
              value={shownMinutes}
              onChange={(event) => setMinutes(event.target.value)}
            />
          ) : null}
        </div>
        {moving === undefined && data ? (
          <>
            <Select
              id={`${ids}-colleague`}
              label={t("appointments.dialog.colleague")}
              value={colleague}
              options={[{ value: "", label: t("appointments.dialog.noColleague") }, ...data.colleagues.map((person) => ({ value: person.id, label: person.displayName }))]}
              onChange={setColleague}
            />
            {leadEmail === null ? (
              <TextField
                id={`${ids}-email`}
                label={t("appointments.dialog.leadEmail")}
                hint={t("appointments.dialog.leadEmailHint")}
                type="email"
                autoComplete="off"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            ) : null}
          </>
        ) : null}
        {options.isError ? (
          <div className="g-appointment-form__row">
            <Reason tone="warning" role="alert">
              {messageForError(t, options.error)}
            </Reason>
            <Button onClick={() => void options.refetch()}>{t("common.retry")}</Button>
          </div>
        ) : null}
        {data && !data.canBook ? <Reason tone="warning">{t("errors.permission_denied")}</Reason> : outcome ? <Reason>{outcome}</Reason> : null}
        <div className="g-appointment-form__actions">
          <Button variant="primary" type="submit" disabled={pending || (moving === undefined && (!data || !data.canBook))}>
            {moving ? t("appointments.dialog.move") : t("appointments.dialog.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
